import { useEffect, useRef } from "react";
import { getLocalized, menuCategories } from "../../data/menu";

function MenuItemDetailModal({ item, lang, t, onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!item) return undefined;

    const previouslyFocused = document.activeElement;
    closeRef.current?.focus();

    function onKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [item, onClose]);

  if (!item) return null;

  const name = getLocalized(item.name, lang);
  const fullDescription =
    getLocalized(item.fullDescription, lang) ||
    getLocalized(item.description, lang);
  const ingredients = getLocalized(item.ingredients, lang);
  const allergens = getLocalized(item.allergens, lang);
  const priceNote = getLocalized(item.priceNote, lang);
  const category = menuCategories.find((c) => c.id === item.categoryId);
  const categoryLabel = category
    ? category.label[lang] || category.label.hu
    : "";
  const categoryNote = category ? getLocalized(category.note, lang) : "";

  return (
    <div className="menu-modal-root" role="presentation">
      <button
        type="button"
        className="menu-modal-backdrop"
        aria-label={t.closeDetails}
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        className={`menu-modal${item.image ? "" : " menu-modal--text"}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="menu-modal-title"
      >
        <button
          ref={closeRef}
          type="button"
          className="menu-modal-close"
          onClick={onClose}
        >
          {t.closeDetails}
        </button>

        {item.image ? (
          <div className="menu-modal-media">
            <img src={item.image} alt={name} className="menu-modal-image" />
          </div>
        ) : null}

        <div className="menu-modal-body">
          {categoryLabel ? (
            <p className="menu-modal-category">{categoryLabel}</p>
          ) : null}
          <h3 id="menu-modal-title" className="menu-modal-title">
            {name}
          </h3>
          {fullDescription ? (
            <p className="menu-modal-text">{fullDescription}</p>
          ) : null}
          {categoryNote ? (
            <p className="menu-modal-note">{categoryNote}</p>
          ) : null}

          {Array.isArray(ingredients) && ingredients.length > 0 && (
            <div className="menu-modal-block">
              <h4>{t.ingredients}</h4>
              <p>{ingredients.join(" · ")}</p>
            </div>
          )}

          {Array.isArray(allergens) && allergens.length > 0 && (
            <div className="menu-modal-block">
              <h4>{t.allergens}</h4>
              <p>{allergens.join(" · ")}</p>
            </div>
          )}

          <p className="menu-modal-price">
            <span>{t.price}</span>
            <strong>
              {item.price}
              {priceNote ? (
                <span className="menu-modal-price-note"> {priceNote}</span>
              ) : null}
            </strong>
          </p>
        </div>
      </div>
    </div>
  );
}

export default MenuItemDetailModal;
